import React, { useState, useRef, useEffect } from 'react';
import ChatHeader from './ChatHeader';
import ChatMessage from './ChatMessage';
import MessageInput from './MessageInput';
import QuickReplyButton from './QuickReplyButton';
import { Message } from '../types';
import { generateId } from '../utils/generateId';
import { useAdmin } from '../context/AdminContext';

const ChatBox: React.FC = () => {
  const { quickReplies, responsePatterns, welcomeMessages } = useAdmin();
  
  const getActiveWelcomeMessage = () => {
    const activeMessage = welcomeMessages.find(msg => msg.is_active);
    return activeMessage?.message || 'Merhaba! Size nasıl yardımcı olabilirim?';
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      text: getActiveWelcomeMessage(),
      timestamp: new Date(),
    },
  ]);
  const [showReplies, setShowReplies] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Karşılama mesajı değiştiğinde güncelle
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].id === '1') {
        return [{
          id: '1',
          type: 'bot',
          text: getActiveWelcomeMessage(),
          timestamp: new Date(),
        }];
      }
      return prev;
    });
  }, [welcomeMessages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const getResponseForMessage = (message: string): string => {
    const lowerMessage = message.toLowerCase();
    
    // First check if the message exactly matches a quick reply
    const matchingQuickReply = quickReplies.find(reply => 
      reply.text.toLowerCase() === lowerMessage
    );
    
    if (matchingQuickReply && matchingQuickReply.response) {
      return matchingQuickReply.response;
    }
    
    // Then check response patterns for partial matches
    for (const pattern of responsePatterns) {
      const triggers = pattern.trigger.split(',');
      if (triggers.some(trigger => lowerMessage.includes(trigger.trim().toLowerCase()))) {
        return pattern.response;
      }
    }
    
    // Finally check if any quick reply text is contained in the message
    for (const reply of quickReplies) {
      if (lowerMessage.includes(reply.text.toLowerCase()) && reply.response && reply.response.trim() !== '') {
        return reply.response;
      }
    }
    
    return 'Bu konuda net bir bilgi veremiyorum. Daha fazla bilgi için 0462 123 45 67 numaralı telefonu arayabilirsiniz.';
  };

  // Rastgele soru seçimi fonksiyonu
  const getRandomQuestions = (count: number) => {
    const shuffled = [...quickReplies].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Link'i mesajdan çıkarma fonksiyonu
  const extractAndRemoveLink = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const link = text.match(urlRegex)?.[0];
    const textWithoutLink = text.replace(urlRegex, '').trim();
    return { textWithoutLink, link };
  };

  const handleSendMessage = (text: string) => {
    const userMessage: Message = {
      id: generateId(),
      type: 'user',
      text,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setShowReplies(false);
    setIsTyping(true);
    
    setTimeout(() => {
      const response = getResponseForMessage(text);
      const { textWithoutLink, link } = extractAndRemoveLink(response);
      const hasLink = !!link;
      
      const botResponse: Message = {
        id: generateId(),
        type: 'bot',
        text: textWithoutLink,
        timestamp: new Date(),
        hasLink: hasLink,
        originalResponse: response
      };
      
      setMessages(prev => [...prev, botResponse]);
      setShowReplies(true);
      setIsTyping(false);
    }, 1500);
  };
  
  return (
    <div className="flex flex-col h-full w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
      <ChatHeader />
      
      <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-white scrollbar-hide" style={{ minHeight: 0 }}>
        {messages.map(message => (
          <ChatMessage key={message.id} message={message} />
        ))}
        
        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="flex-shrink-0 mr-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-600 to-red-500 flex items-center justify-center text-white shadow-lg">
                <span className="text-sm font-bold">ÇA</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl rounded-tl-md p-4 shadow-md border border-gray-100 max-w-[80%]">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        {showReplies && !isTyping && messages.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {/* İlk mesajda 5 soru, sonraki mesajlarda 3 soru göster */}
            {getRandomQuestions(messages.length === 1 ? 5 : 3).map(reply => (
              <QuickReplyButton 
                key={reply.id} 
                reply={reply} 
                onClick={handleSendMessage} 
              />
            ))}
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="flex-shrink-0">
        <MessageInput onSendMessage={handleSendMessage} />
      </div>
    </div>
  );
};

export default ChatBox;